import { Component, OnInit, ViewChild } from '@angular/core';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { UntypedFormControl } from '@angular/forms';
import { environment } from '@env';
import { SmsTypeStatistic } from '@models/statistics/statistics.model';
import { SubaccountStatistics } from '@models/subaccount/statistics/subaccount-statistics';
import { ApiGetSubaccounts } from '@models/subaccount/subaccount';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { SubaccountStatisticsService } from '@services/subaccount-statistics.service';
import { PrefixLabels } from '@services/subaccount.service';
import { AtlasGridOptions } from '@ui/components/atlas-grid/altas-grid-options.model';
import { AtlasGridComponent } from '@ui/components/atlas-grid/atlas-grid.component';
import { ButtonGroupRendererComponent } from '@ui/components/atlas-grid/renderer/buttongroup-renderer.component';
import { AtlasGridHtmlRendererComponent } from '@ui/components/atlas-grid/renderer/html-renderer.component';
import { RangeFilter } from '@ui/components/month-picker-range/month-picker-range.component';
import { formatIntNumber } from '@utils/utils';
import { ChartDataset, ChartOptions, ChartType, TooltipItem } from 'chart.js';
import * as moment from 'moment';
// import { Color, Label } from 'ng2-charts';
import { Subject } from 'rxjs';
import { debounceTime, finalize } from 'rxjs/operators';
import { GridReadyEvent } from 'ag-grid-community';
import { LoaderService } from '@services/loader.service';
// import { LoaderService } from '@services/loader.service';

@UntilDestroy()
@Component({
  selector: 'wsb-reseller-statistics',
  templateUrl: './subaccount-statistics.component.html',
  styleUrls: ['./subaccount-statistics.component.scss'],
  animations: [
    trigger('gridIsLoaded', [
      state('hide', style({ opacity: 0 })),
      state('show', style({ opacity: 1 })),
      transition('hide => show', animate('150ms', style({ opacity: 1 }))),
      transition('show => hide', animate('150ms', style({ opacity: 0 })))
    ])
  ],
  standalone: false
})
export class SubaccountStatisticsComponent implements OnInit {
  activeId = 1;
  rangeFilter: RangeFilter;
  dayMonthToggle = new UntypedFormControl(false);

  components = {
    buttonGroupRenderer: ButtonGroupRendererComponent,
    htmlRenderer: AtlasGridHtmlRendererComponent
  };

  // graphics elements
  public lineChartLegend = false;
  public lineChartType: ChartType = 'line';
  public lineChartOptions?: ChartOptions;
  public lineChartData: ChartDataset[] = [];
  public lineChartLabels: string[] = [];

  // tab 1
  private search: Subject<string> = new Subject();
  scrollPageSubs = 1;
  idSubSelected = 0;
  subaccountSelected: any;
  messageTypeStatistics?: SmsTypeStatistic[];
  subaccounts: Array<SubaccountStatistics> = [];
  backupSubs: Array<SubaccountStatistics> = [];
  totalsMessageTypes = 0;
  // table TabSubaccountStatistics
  @ViewChild('grid') grid: AtlasGridComponent;

  tableColumnDefs = null;
  tableGridOptions = null;
  paginationPageSize = 10;
  gridAnimationState = 'hide';

  constructor(
    private subStatisticsService: SubaccountStatisticsService,
    private translateService: TranslateService,
    private loaderService: LoaderService
  ) {}

  ngOnInit(): void {
    this.rangeFilter = {
      from: new Date(),
      to: new Date()
    };

    this.updateAxes();
    this.dayMonthToggle.valueChanges.pipe(untilDestroyed(this)).subscribe(() => this.onRangeChanged(this.rangeFilter));
    this.initArraySubaccounts();
    this.searchDebounce();
    this.initTabTableSubStatistics();
  }

  private get allSubs() {
    return this.subaccounts.filter(sub => sub.login === this.getLabels('reseller_senders_new_subaccount_all'));
  }

  private initTabTableSubStatistics() {
    const that = this;
    this.tableColumnDefs = [
      {
        headerNameTranslate: 'account_userid',
        suppressSizeToFit: true,
        sortable: false,
        field: 'idSubaccountUser'
      },
      {
        headerNameTranslate: 'settings_subaccounts_table_field_details',
        field: 'login',
        colPrefKey: 'login',

        sortable: false,
        cellRenderer: sub => {
          if (sub && sub.value) {
            return (
              '<b>' +
              (sub.data?.login ? sub.data?.login : '') +
              '</b><br>' +
              (sub.data?.name ? sub.data?.name : '') +
              ' ' +
              (sub.data?.surname ? sub.data?.surname : '') +
              '<br>' +
              (sub.data?.company ? sub.data?.company : '')
            );
          }
          return '';
        },
        cellStyle: { 'line-height': '20px' }
      },
      {
        headerNameTranslate: 'settings_subaccounts_table_field_sent',
        field: 'total',
        resizable: false,
        suppressSizeToFit: true,
        sortable: false,

        cellRenderer: sub => {
          return this.getTotalsSMS(sub.data);
        },
        cellStyle: { 'line-height': '20px' }
      }
    ];

    this.tableGridOptions = AtlasGridOptions.deserialize({
      onGridReady: (event: GridReadyEvent) => {
        this.gridAnimationState = 'show';
      },
      pagination: true,
      paginationPageSize: this.paginationPageSize,
      rowHeight: 115,
      sortable: true,
      suppressRowClickSelection: true,
      loadFunction: this.reloadList.bind(that),
      filters: this.getFilters()
      // onFilterChange: () => {
      //   this.grid.options.filters[1].min = this.grid.filtersModel.from;
      //   this.grid.options.filters[0].max = this.grid.filtersModel.to;
      //   }
    });
  }

  private initArraySubaccounts() {
    this.translateService.get('reseller_senders_new_subaccount_all').subscribe(label => {
      this.subaccounts = [
        {
          idSubaccountUser: 0,
          login: label
        } as SubaccountStatistics
      ];
      this.subStatisticsService.getListSubaccountStatistics().subscribe(subs => {
        this.subaccounts = [...this.allSubs, ...subs.data];
        this.backupSubs = this.subaccounts;
      });
    });
  }

  private getTotalsSMS(valueData?) {
    const data = valueData;
    if (data) {
      const totalsSMS = data.totalsSMSStatistics;
      if (totalsSMS) {
        return this.getTotalsSMSPricePerMessages(totalsSMS);
      }
    }
    return '<b>' + this.getLabels('stats_sms_sent_total') + ': ' + '</b>' + '0';
  }

  private getTotalsSMSPricePerMessages(totalsSMS) {
    const totals = totalsSMS.totals;
    const detailsSMS = totalsSMS.detailsSmsStatistics;
    let result = '<b>' + this.getLabels('stats_sms_sent_total') + ': ' + '</b>' + totals + '<br>';
    detailsSMS.forEach(detail => {
      const mt = detail.idMessageType.toLowerCase();
      const totalMt = detail.total;
      result = result + this.getLabels(PrefixLabels.CREDIT_TYPE + mt) + ': ' + totalMt + '<br>';
    });
    return result;
  }

  formatNumber(n: number): string {
    return formatIntNumber(n);
  }

  // ng-select

  private searchDebounce() {
    this.search.pipe(debounceTime(250)).subscribe(filter => {
      const body: ApiGetSubaccounts = {
        login: filter
      } as ApiGetSubaccounts;
      this.subStatisticsService.getListSubaccountStatistics(null, null, null, null, null, null, null, null, body).subscribe(subs => {
        this.subaccounts = [...this.allSubs, ...subs.data];
      });
    });
  }

  onChangeGrapichs() {
    this.onRangeChanged(this.rangeFilter);
  }

  onSearch($event) {
    this.search.next($event.term);
  }

  onScrollToEnd() {
    this.scrollPageSubs++;
    this.subStatisticsService.getListSubaccountStatistics(this.scrollPageSubs).subscribe(subs => {
      this.subaccounts = [...this.subaccounts, ...subs.data];
    });
  }

  onClose() {
    this.scrollPageSubs = 1;
    this.subaccounts = this.backupSubs;
  }

  // graphics

  private updateAxes(): void {
    this.lineChartOptions = {
      elements: {
        line: {
          tension: 0
        }
      },
      plugins: {
        tooltip: {
          mode: 'index',
          callbacks: {
            title(context: TooltipItem<ChartType>[]): string | string[] {
              return `${context[0].label} - ${context[0].raw}`;
            },
            label(context: TooltipItem<ChartType>): string | string[] {
              return undefined;
            }
          }
        }
      },
      responsive: true,
      scales: {
        y: {
          ticks: { stepSize: 1, maxTicksLimit: 10 }
        },
        x: {
          grid: { color: 'rgba(255,0,0,0.0)' }
        }
      }
    };
  }

  onRangeChanged(rangeFilter: RangeFilter): void {
    const id = this.idSubSelected !== 0 ? this.idSubSelected : null;
    this.rangeFilter = rangeFilter;
    this.updateAxes();
    if (this.dayMonthToggle.value) {
      this.fetchMonthlySMSStatistics(id, rangeFilter);
    } else {
      this.fetchDailySMSStatistics(id, rangeFilter);
    }
    this.fetchSMSTypesStatistics(id, rangeFilter);
  }

  private fetchMonthlySMSStatistics(idSubaccountUser, rangeFilter: RangeFilter) {
    this.loaderService.show();
    this.subStatisticsService
      .getMonthlySmsSubStatistics(idSubaccountUser, { dateFrom: rangeFilter.from, dateTo: rangeFilter.to }, true)
      .pipe(
        untilDestroyed(this),
        finalize(() => this.loaderService.hide())
      )
      .subscribe(statistics => {
        this.lineChartData = [
          {
            data: statistics.map(s => s.count),
            label: 'Monthly SMS',
            backgroundColor: 'rgba(0, 0, 0 , 0.0)',
            borderColor: environment.chartsColorPalette[0],
            pointBorderColor: environment.chartsColorPalette[0],
            pointHoverBorderColor: 'rgba(148,159,177,0.8)'
          }
        ];
        this.lineChartLabels = statistics.map(s => s.label);
      });
  }

  private fetchDailySMSStatistics(idSubaccountUser, rangeFilter: RangeFilter) {
    this.loaderService.show();

    this.subStatisticsService
      .getDailySmsSubStatistics(idSubaccountUser, { dateFrom: rangeFilter.from, dateTo: rangeFilter.to }, true)
      .pipe(
        untilDestroyed(this),
        finalize(() => this.loaderService.hide())
      )
      .subscribe(statistics => {
        this.lineChartData = [{ data: statistics.map(s => s.count), label: 'Daily SMS' }];
        this.lineChartLabels = statistics.map(s => s.label);
      });
  }

  private fetchSMSTypesStatistics(idSubaccountUser, rangeFilter: RangeFilter) {
    this.loaderService.show();

    this.subStatisticsService
      .getTypeSmsSubStatistics(idSubaccountUser, { dateFrom: rangeFilter.from, dateTo: rangeFilter.to })
      .pipe(finalize(() => this.loaderService.hide()))
      .subscribe(statistics => {
        this.messageTypeStatistics = statistics;
        this.totalsMessageTypes = this.messageTypeStatistics.reduce((sum, current) => sum + current.count, 0);
        this.loaderService.hide();
      });
  }

  // table subaccount statistics
  private getLabels(value) {
    let labels;
    this.translateService.get(value).subscribe(label => {
      labels = label;
    });
    return labels;
  }

  private reloadList(startRow?, endRow?, sort?) {
    this.loaderService.show();
    return this.subStatisticsService
      .getListSubaccountStatistics(
        1 + startRow / (endRow - startRow),
        endRow - startRow,
        // sort column
        sort && sort.length ? this.getColId(sort[0].colId) : null,
        sort && sort.length ? (sort[0].sort === 'desc' ? 'd' : 'a') : null,
        this.getFilterFromMonth(),
        this.getFilterFromYear(),
        this.getFilterToMonth(),
        this.getFilterToYear(),
        // filters fields:if filters is empty then nobody filter is applied
        this.geFiltersToSearch()
      )
      .pipe(finalize(() => this.loaderService.hide()));
  }

  getFilterFromMonth(): string {
    const date = this.grid.filtersModel.from;
    if (date) {
      const month = moment(date).month() + 1;
      return month.toString();
    }
    return null;
  }

  getFilterFromYear(): string {
    const date = this.grid.filtersModel.from;
    if (date) {
      const year = moment(date).year();
      return year.toString();
    }
    return null;
  }

  getFilterToMonth(): string {
    const date = this.grid.filtersModel.to;
    if (date) {
      const month = moment(date).month() + 1;
      return month.toString();
    }
    return null;
  }

  getFilterToYear(): string {
    const date = this.grid.filtersModel.to;
    if (date) {
      const year = moment(date).year();
      return year.toString();
    }
    return null;
  }

  geFiltersToSearch() {
    let apiGetSubaccounts = new ApiGetSubaccounts();
    const basicFilters = {};
    ['login', 'name', 'surname', 'company'].map(x => {
      basicFilters[x] = this.grid.filtersModel[x]?.value ? this.grid.filtersModel[x]?.value : this.grid.filtersModel[x];
    });
    // if basicFilters is empty then nobody filter is applied
    apiGetSubaccounts = basicFilters;
    return apiGetSubaccounts;
  }

  getColId(id: string) {
    if (id === 'idRateProfile') {
      return 'LOGIN';
    }
  }

  getFilters() {
    return [
      {
        name: 'from',
        label: 'common_from',
        type: 'dateMonthAndYear'
      },
      {
        name: 'to',
        label: 'common_to',
        type: 'dateMonthAndYear'
      },
      {
        name: 'login',
        label: 'account_login',
        type: 'text'
      },
      {
        name: 'name',
        label: 'common_name',
        type: 'text'
      },
      {
        name: 'surname',
        label: 'common_surname',
        type: 'text'
      },
      {
        name: 'company',
        label: 'account_company',
        type: 'text'
      }
    ];
  }
}
